'use client';
import React from 'react';
import { getPieceDistribution } from '@/lib/gameEngine';

type Props = {
  selected: number;
  onSelect: (count: number) => void;
};

const COUNT_INFO: Record<number, { label: string; description: string }> = {
  2: { label: '1v1 Duel', description: 'Classic head-to-head. 9 vs 8 pieces.' },
  3: { label: 'Triangle Match', description: 'Three-way rivalry. 6 / 6 / 5 pieces.' },
  4: { label: 'Four-Way Battle', description: 'High chaos, strategic depth. 5/4/4/4 pieces.' },
  5: { label: 'Grand Tournament', description: 'Maximum players. 4/4/3/3/3 pieces.' },
};

export default function PlayerCountStep({ selected, onSelect }: Props) {
  return (
    <div className="animate-fade-in">
      <div className="text-center mb-10">
        <h2 className="text-hero-md text-foreground mb-3">How Many Players?</h2>
        <p className="text-muted-foreground">
          Each player count has a different piece distribution per official ViNCi rules.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {[2, 3, 4, 5].map((count) => {
          const info = COUNT_INFO[count];
          const distribution = getPieceDistribution(count);
          const isSelected = selected === count;
          return (
            <button
              key={`count-${count}`}
              onClick={() => onSelect(count)}
              className={`relative p-6 rounded-2xl border text-left transition-all duration-200 active:scale-98 ${
                isSelected
                  ? 'border-primary bg-primary/10 glow-primary' :'border-border bg-card hover:border-primary/40 hover:bg-secondary/50'
              }`}
            >
              {isSelected && (
                <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}

              <div className="text-4xl font-800 font-mono-game text-primary mb-2">{count}</div>
              <div className="font-700 text-foreground mb-1">{info.label}</div>
              <div className="text-sm text-muted-foreground mb-3">{info.description}</div>

              {/* Piece distribution visual */}
              <div className="flex flex-wrap gap-1">
                {distribution.map((pieces, pi) => (
                  <div
                    key={`dist-${count}-p${pi}`}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-600"
                    style={{
                      background: `var(--player-${pi + 1})20`,
                      color: `var(--player-${pi + 1})`,
                      border: `1px solid var(--player-${pi + 1})30`,
                    }}
                  >
                    P{pi + 1}: {pieces}
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}